import {
  success, warning, error as errorColor, infoBlue, accent, textSecondary,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * Density tokens for the record.
 *
 * The rest of the portal runs 16px radii, p:3 and ~six numbers a screen. A
 * record is scanned, not read — a CFI opens it three minutes before a session
 * — so it runs tighter: 10px radii, 12–13px type, more per screen. These are
 * separate tokens rather than edits to the shared theme so the change is
 * confined to the surface it was decided for.
 */
export const card = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E8E4DF",
  borderRadius: "10px",
  padding: "14px 16px",
};
export const cardTight = { ...card, padding: "10px 12px" };
export const label = {
  fontSize: "10.5px",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: textSecondary,
};
export const value = { fontSize: "13px", fontWeight: 600, color: "#1a1a1a" };
export const meta = { fontSize: "11.5px", color: textSecondary };

/**
 * Every timeline entry type, in one table.
 *
 * `kind` comes straight from services/studentRecordService.js — one entry
 * shape, one renderer, one filter list. Adding a type there means adding a row
 * here and nothing else; the previous design needed the same change in three
 * places and quietly diverged between the school and instructor views.
 */
export const KIND = {
  session:        { label: "Session",   color: infoBlue,     group: "sessions" },
  summary:        { label: "Summary",   color: accent,       group: "sessions" },
  group_session:  { label: "Group",     color: infoBlue,     group: "sessions" },
  session_offer:  { label: "Offer",     color: warning,      group: "signals"  },
  written_result: { label: "Written",   color: success,      group: "signals"  },
  task:           { label: "Task",      color: accent,       group: "actions"  },
  note:           { label: "Note",      color: textSecondary, group: "actions" },
  // Same collection as a note, different act — visibleToStudent is what tells
  // them apart, and until the record read it an instructor could not see what
  // the student had already been told.
  message:        { label: "Message",   color: infoBlue,     group: "comms"   },
  email:          { label: "Email",     color: textSecondary, group: "comms"   },
  payment_failed: { label: "Payment",   color: errorColor,   group: "money"    },
};

export const FILTERS = [
  { key: "all",      label: "Everything" },
  { key: "sessions", label: "Sessions" },
  { key: "signals",  label: "Signals" },
  { key: "actions",  label: "Notes & tasks" },
  { key: "comms",    label: "Messages & emails" },
  { key: "money",    label: "Money" },
];

export const kindMeta = (k) => KIND[k] || { label: k, color: textSecondary, group: "other" };

/**
 * A topic key as a person reads it. "taf-metar" -> "Taf metar", and "all" —
 * the sentinel the practice screen files whole-subject sessions under — as
 * "Mixed practice", because it is most of what a real student produces and
 * hiding it made subject totals stop adding up on screen.
 */
/**
 * Cased as the industry cases them, never as a title-caser would. "Taf Metar"
 * is not a thing, and sentence-casing the whole key produced exactly that.
 */
const ACRONYMS = new Set([
  "taf", "metar", "sigmet", "airmet", "notam", "aip", "airac", "aic",
  "vfr", "ifr", "svfr", "vmc", "imc", "sera", "ats", "atc", "atz", "ctr",
  "rmz", "tmz", "ssr", "sar", "icao", "caa", "faa", "easa",
  "qnh", "qfe", "qne", "msa", "mtow", "poh",
  "ppl", "cpl", "atpl", "acs", "rt", "vor", "ndb", "dme", "gps", "gnss",
  "asi", "vsi", "di", "cb", "uk", "us", "eta", "etd", "tas", "ias", "cas",
]);

/** Lower case inside a name, never at the start of one. */
const MINOR = new Set(["of", "and", "the", "in", "on", "a", "an", "to", "for", "at", "by", "with", "or"]);

/**
 * A subject or topic key, written the way a person would write it.
 *
 * The keys are kebab-case because that is what the question bank tags
 * questions with. Sentence-casing them gave "Taf metar" and "Class2 medical"
 * on the one screen an instructor reads out loud to a student.
 */
export const prettyName = (raw) => {
  if (!raw) return "";
  return String(raw)
    .replace(/[_-]+/g, " ")
    // "class2" -> "class 2". The key has no room for the space; the reader
    // expects one.
    .replace(/([a-zA-Z])(\d)/g, "$1 $2")
    .replace(/(\d)([a-zA-Z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      if (i > 0 && MINOR.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

export const humaniseTopic = (t) => (t === "all" ? "Mixed practice" : prettyName(t));
